import { TMentionUser } from '@onesource/schemas';
import { MentionNodeAttrs } from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import tippy, { Instance as TippyInstance } from 'tippy.js';

import 'tippy.js/dist/tippy.css';
import { MentionList, MentionListRef } from './mention-list';

import './mention-tippy.css';

export type TFetchMentionUsers = (query: string) => Promise<TMentionUser[]>;

const MENTION_FETCH_DEBOUNCE_MS = 1000;

export const buildMentionSuggestion = (fetchUsers: TFetchMentionUsers, debounceMs = MENTION_FETCH_DEBOUNCE_MS) => {
  // Per-editor debounce state. Every caller waits on the same trailing-edge fetch:
  // when the timer eventually fires we hit the backend once with the latest query
  // and resolve all queued promises. This is critical because tiptap's first
  // `update` (handleStart) blocks `onStart` behind its `await items(...)` — if any
  // call were left unresolved, the popup would never be created.
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingResolvers: Array<(users: TMentionUser[]) => void> = [];
  let pendingQuery = '';

  const debouncedFetch = (query: string): Promise<TMentionUser[]> =>
    new Promise<TMentionUser[]>(resolve => {
      pendingResolvers.push(resolve);
      pendingQuery = query;

      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(async () => {
        const resolvers = pendingResolvers;
        const queryToFetch = pendingQuery;
        pendingResolvers = [];
        timer = null;
        try {
          const users = await fetchUsers(queryToFetch);
          for (const r of resolvers) {
            r(users);
          }
        } catch {
          for (const r of resolvers) {
            r([]);
          }
        }
      }, debounceMs);
    });

  return {
    allowSpaces: true,
    items: ({ query }: { query: string }): Promise<TMentionUser[]> =>
      query.length < 3 ? Promise.resolve([]) : debouncedFetch(query),

    render: () => {
      let component: ReactRenderer<MentionListRef>;
      let popup: TippyInstance[];

      const createPopup = (props: SuggestionProps<TMentionUser, MentionNodeAttrs>, loading: boolean) => {
        component = new ReactRenderer(MentionList, {
          props: { ...props, loading },
          editor: props.editor,
        });

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'mention',
        });
      };

      return {
        // Fires before items() blocks — create popup with loader immediately
        onBeforeStart: (props: SuggestionProps<TMentionUser, MentionNodeAttrs>) => {
          createPopup(props, props.query.length >= 3);
        },

        // Fires after items() resolves — update with results
        onStart: (props: SuggestionProps<TMentionUser, MentionNodeAttrs>) => {
          component.updateProps({ ...props, loading: false });
          popup[0].setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },

        // Fires before items() on each subsequent keystroke
        onBeforeUpdate: (props: SuggestionProps<TMentionUser, MentionNodeAttrs>) => {
          if (!component) {
            return;
          }
          component.updateProps({ loading: props.query.length >= 3 });
          popup[0].setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },

        // Fires after items() resolves on subsequent keystrokes
        onUpdate: (props: SuggestionProps<TMentionUser, MentionNodeAttrs>) => {
          component.updateProps({ ...props, loading: false });
          popup[0].setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },

        onKeyDown: (props: SuggestionKeyDownProps) => {
          if (props.event.key === 'Escape') {
            popup[0].hide();
            return true;
          }
          return component?.ref?.onKeyDown?.(props) ?? false;
        },

        onExit: () => {
          popup[0].destroy();
          component.destroy();
        },
      };
    },
  };
};
